import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Custom hook to manage user onboarding state
 * @returns {Object} Onboarding state and handlers
 */
const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // =====================================================================
  // ⚠️ TESTING MODE disabled for production ⚠️
  // =====================================================================
  const TESTING_MODE = false;
  
  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (TESTING_MODE) {
        setCurrentUser(user || null);
        setShowOnboarding(true);
        setIsLoading(false);
        return;
      }
      if (!user) {
        setIsLoading(false);
        setShowOnboarding(false);
        return;
      }
      setCurrentUser(user);
      
      try {
        // Check if user has completed onboarding
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // If this is a first login (no onboarding flag exists)
          if (!userData.hasOwnProperty('onboardingCompleted')) {
            // Create or update onboarding flag for first-time users
            await updateDoc(userRef, {
              onboardingCompleted: false,
              onboardingLastUpdated: new Date().toISOString()
            });
            setShowOnboarding(true);
          } 
          // Or if onboarding was explicitly not completed
          else if (userData.onboardingCompleted === false) {
            setShowOnboarding(true);
          }
        } else {
          // New user - create profile document with onboarding flag
          try {
            await setDoc(userRef, {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || '',
              photoURL: user.photoURL || '',
              onboardingCompleted: false,
              onboardingLastUpdated: new Date().toISOString(),
              createdAt: new Date().toISOString()
            });
            setShowOnboarding(true);
          } catch (error) {
            console.error("Error creating new user profile:", error);
          }
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      } finally {
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [TESTING_MODE]);

  const completeOnboarding = async () => {
    if (!currentUser) return;
    
    try {
      setIsLoading(true);
      
      // Only update Firestore if not in testing mode
      if (!TESTING_MODE) {
        // Update user document to mark onboarding as complete
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, { 
          onboardingCompleted: true,
          onboardingLastUpdated: new Date().toISOString()
        });
      }
      
      setShowOnboarding(false);
    } catch (error) {
      console.error("Error completing onboarding:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetOnboarding = async () => {
    if (!currentUser) return;
    
    try {
      setIsLoading(true);
      
      // Only update Firestore if not in testing mode
      if (!TESTING_MODE) {
        // Reset onboarding state to show it again
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, { 
          onboardingCompleted: false,
          onboardingLastUpdated: new Date().toISOString()
        });
      }
      
      setShowOnboarding(true);
    } catch (error) {
      console.error("Error resetting onboarding:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    showOnboarding,
    isLoading,
    currentUser,
    completeOnboarding,
    resetOnboarding
  };
};

export default useOnboarding; 